import { Check } from "lucide-react";
import { moneyDisplay, qtyDisplay, ratioPct } from "@icraftx/shared/v1";
import { ProgressBar } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

/**
 * Compact current/total + bar for pay / deliver / receive axes (plan/30).
 * Complete (100%) → check + total only. Lists use column headers for the
 * axis name; detail headers pass `label`.
 */
export function AxisProgress(props: {
  current: string | number | null | undefined;
  max: string | number | null | undefined;
  /** Screen-reader + tooltip context, e.g. "Paid" */
  axis: string;
  /** money = 2 dp; qty = trimmed qty display */
  format: "money" | "qty";
  /** Visible label above the bar (detail headers). */
  label?: string;
  /** Click filters the list by this axis value (toggle). */
  onFilterClick?: () => void;
  className?: string;
}) {
  const pct = ratioPct(props.current, props.max);
  if (pct == null || props.current == null || props.max == null) {
    return (
      <span className={cn("text-[var(--color-muted)]", props.className)}>
        —
      </span>
    );
  }

  const currentLabel =
    props.format === "qty"
      ? qtyDisplay(props.current)
      : moneyDisplay(props.current);
  const maxLabel =
    props.format === "qty" ? qtyDisplay(props.max) : moneyDisplay(props.max);
  const complete = pct >= 100;
  const display = complete ? maxLabel : `${currentLabel} / ${maxLabel}`;
  const aria = complete
    ? `${props.axis} complete · ${maxLabel}`
    : `${props.axis} ${display}`;
  const title = props.onFilterClick ? `Filter: ${props.axis}` : aria;

  const inner = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        {props.label ? (
          <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
            {props.label}
          </span>
        ) : null}
        <span
          className={cn(
            "inline-flex min-w-0 items-center gap-1 font-mono text-xs tabular-nums whitespace-nowrap",
            !props.label && "ml-auto",
            complete
              ? "text-[var(--color-ok)]"
              : "text-[var(--color-foreground)]",
          )}
        >
          {complete ? (
            <Check
              className="size-3.5 shrink-0"
              strokeWidth={2.5}
              aria-hidden
            />
          ) : null}
          <span className="min-w-0 truncate">{display}</span>
        </span>
      </div>
      {complete ? null : (
        <ProgressBar
          value={pct}
          aria-label={aria}
          className="mt-0.5 w-full space-y-0"
        />
      )}
    </>
  );

  if (props.onFilterClick) {
    return (
      <button
        type="button"
        className={cn(
          "w-full min-w-[5.5rem] cursor-pointer rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
          props.className,
        )}
        title={title}
        onClick={props.onFilterClick}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={cn("w-full min-w-[5.5rem]", props.className)} title={title}>
      {inner}
    </div>
  );
}
